import React, {useState, useEffect, useCallback} from 'react';
import ButtonContainer from '../../../../components/Simple/ButtonContainer';
import SMCButton from '../../../../components/Simple/SMCButton';
import {useTranslation} from 'react-i18next';
import LabelContainer from '../../../../components/Page/LabelContainer';
import SMCTable from '../../../../components/core/SMCTable/SMCTable';
import Api from '../../../../api/SMPFetchHelper';
import {getErrorCode} from '../../../../utils/utils';
import {useSMCSnackbar, useBoolean} from '../../../../utils/hooks';
import {ErrorCodes} from '../../../../api/consts';
import LoadingContainer from '../../../../components/Page/LoadingContainer';
import SMCIcon from '../../../../components/Simple/SMCIcon';
import enabledLogo from '../../../../assets/images/plan/enabled.png';
import disabledLogo from '../../../../assets/images/plan/disabled.png';
import localLogo from '../../../../assets/images/plan/local.png';
import {SMCPaths} from '../../../../conf/Routes';
import {Link} from 'react-router-dom';
import SetGatewayModal from '../../../PopupPages/SetGatewayModal';
import {useSMCErrorHandler} from '../../../../utils/errorUtils';
import SelectInputModal from '../../../PopupPages/SelectInputModal';
import {SMCOption} from '../../../../components/Simple/SMCSelect';


const nameCell = React.memo(({object, value}) => {
    if (value) {
        const memberType = object['memberType'];
        if (memberType === 'GATEWAY') {
            return <Link to={`${SMCPaths.gateways}?id=${value}`}><SMCIcon icon="edit" align="absmiddle" /> {value}</Link>
        }
        if (memberType === 'COMMUNITY') {
            return <Link to={`${SMCPaths.communities}?id=${value}`} ><SMCIcon icon="edit" align="absmiddle" /> {value}</Link>
        }
    }
    return null;
});

const memberTypeCell = React.memo(({value}) => {
    const { t } = useTranslation();
    if (value === 'GATEWAY') {
        return <span>{t('com.sofaware.util.TYPE_GATEWAY')}</span>
    }
    if (value === 'COMMUNITY') {
        return <span>{t('com.sofaware.util.TYPE_COMMUNITY')}</span>
    }
    return null;
});

const CenterCell = React.memo(({value}) => {
    if (value) {
        return <SMCIcon icon="greenCheck" alt="center" />
    }
    return null;
});

const IpAddressCell = React.memo(({object}) => {
    const { t } = useTranslation();
    if (object['staticIpAddress']) {
        return object['staticIpAddress'] + ' ' + t('SMC_COMMUNITY_STATIC_IP');
    }
    if (object['dynamicIpAddress']) {
        return object['dynamicIpAddress'];
    }
    return null;
});

const EncryptionDomainCell = React.memo(({value}) => {
    if (value) {
        return value.map((encryptionDomain, index) => (
            <React.Fragment key={index}>
                {index !== 0 && <br />} 
                <span>{encryptionDomain}</span>
            </React.Fragment>
        ))
    } else {
        return 'N/A';
    }
});

const VpnCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case 'ON':
            return <img src={enabledLogo} alt="on" title={t('SMP.ON')} />;
        case 'OFF':
            return <img src={disabledLogo} alt="off" title={t('SMP.OFF')} />;
        case 'LOCAL':
            return <img src={localLogo} alt="local" title={t('SMP.COMMUNITY.MEMBERS.LOCALLY_MANAGED')} />;
        default:
            return null;
    }
});

const StatusCell = React.memo(({value}) => {
    switch (value) {
        case 'CONNECTED':
            return <SMCIcon icon="connected" />;
        case 'DISCONNECTED':
                return <SMCIcon icon="disconnected" />;
        case 'OK':
            return <SMCIcon icon="greenCheck" alt="ok" />;
        case 'NOT_UP_TO_DATE':
            return <SMCIcon icon="redX" alt="not_up_to_date" />;
        default:
            return null;
    }
});

function CommunityMembers({
    community,
    width,
    height
}) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();
    const { getErrorMessage } = useSMCErrorHandler();

    const columns = [
        { id: 'name',             header: t('SMP.NAME')                               , accessor:'name',              width: 100,                  Cell: nameCell                    },
        { id: 'memberType',       header: t('SMP.PLAN.SERVICES.COMMUNITY.MEMBER_TYPE'), accessor:'memberType',        width: 140,                  Cell: memberTypeCell              },
        { id: 'center',           header: t('SMC_COMMUNITY_CENTER')                   , accessor:'center',            width: 65,  align: 'center', Cell: CenterCell                  },
        { id: 'owner',            header: t('SMC_GATEWAY_OWNER')                      , accessor:'owner',             width: 100,                                                    },
        { id: 'ipAddress',        header: t('SMC_COMMUNITY_GATEWAY_IP')               ,                               width: 100,                  Cell: IpAddressCell               },
        { id: 'encryptionDomain', header: t('SMC_COMMUNITY_GATEWAY_TOPOLOGY')         , accessor:'encryptionDomain',  width: 150,                  Cell: EncryptionDomainCell        },
        { id: 'vpnSiteToSite',    header: t('SMC_GATEWAY_VPN_STATUS')                 , accessor:'vpnSiteToSiteMode', width: 125, align: 'center', Cell: VpnCell                     },
        { id: 'status',           header: t('SMC_GATEWAY_STATUS')                     , accessor:'status',            width: 60,  align: 'center', Cell: StatusCell                  }
    ]
    
    const [members, setMembers] = useState([]);
    const [communitySelectList, setCommunitySelectList] = useState([]);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [selectedObjects, setSelectedObjects] = useState([]);

    const [isAddGatewayModalOpen, openAddGatewayModal, closeAddGatewayModal] = useBoolean(false);
    const [isAddCommunityModalOpen, openAddCommunityModal, closeAddCommunityModal] = useBoolean(false);

    const loadMembers = useCallback(async () => {
        try {
            const [getCommunityMembersResp, getPossibleCommunityMembersListResp] = await Promise.all([
                Api.getCommunityMembers(community),
                Api.getPossibleCommunityMembersList(community)
            ]);
            if (getCommunityMembersResp.data.members) {
                setMembers(getCommunityMembersResp.data.members);
            } else {
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            if (getPossibleCommunityMembersListResp.data.data.communitySelectList) {
                setCommunitySelectList(getPossibleCommunityMembersListResp.data.data.communitySelectList);
            }
        } catch (e) {
            const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
            enqueueErrorSnackbar(t(errorCode));
        }
        setIsPageLoading(false);
    }, [community, enqueueErrorSnackbar, t]);

    const deleteSelectedMembers = useCallback(() => {
        if (!selectedObjects || !selectedObjects.length) {
            alert(t('SMC_GEN_NO_SELECT'));
        }
        else {
            const membersForApi = [];
            selectedObjects.forEach(index => {
                const selectedMember = members[index];
                membersForApi.push({
                    type: selectedMember['memberType'],
                    name: selectedMember['name']
                });
            })
            Api.removeCommunityMembers(community, membersForApi)
            .then(() => {
                loadMembers();
            })
            .catch(e => {
                const errorMessage = getErrorMessage(e);
                enqueueErrorSnackbar(errorMessage);
            });
        }
    }, [community, enqueueErrorSnackbar, getErrorMessage, members, loadMembers, selectedObjects, t]);

    useEffect(() => {
        loadMembers();
    }, [loadMembers])


    return (
        <div style={{ width, height, padding: '0px 60px 20px 20px'}}>
            <LoadingContainer loading={isPageLoading}>
                {/* Header */}
                <div className="ObjectEditorHeader Tabs">
                    <LabelContainer label="Name:" value={community} />
                </div>
                <div>
                    <ButtonContainer>
                        <SMCButton variant="ghost" icon="plus" onClick={openAddGatewayModal}>{t('SMC_COMMUNITY_ADD_GATEWAY')}</SMCButton>
                        <SMCButton variant="ghost" icon="plus" onClick={openAddCommunityModal}>{t('SMC_COMMUNITY_ADD_COMM')}</SMCButton>
                        <SMCButton variant="ghost" icon="delete" onClick={deleteSelectedMembers}>{t('SMC_GEN_REMOVE')}</SMCButton>
                    </ButtonContainer>
                    <SMCTable
                        columns={columns}
                        content={members}
                        width={width-80}
                        rowIdSelector={(obj, i) => i}
                        selectedObjects={selectedObjects}
                        setSelectedObjects={setSelectedObjects}
                        hasCheckAll={true}
                        height={height-120}
                    />
                </div>
            </LoadingContainer>
            {/* Floating windows */}
            <SetGatewayModal
                isOpen={isAddGatewayModalOpen}
                onRequestClose={closeAddGatewayModal}
                selectMultiple={true}
                disableSelectById={members ? (
                        members
                        .filter(member => member['memberType'] === 'GATEWAY')
                        .map(member => member['name'])
                    ) : (
                        undefined
                )}
                onSuccess={gwNames => {
                    closeAddGatewayModal();
                    setIsPageLoading(true);
                    Api.addGatewaysToCommunityMembers(community, gwNames)
                    .then(resp => {
                        loadMembers();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoading(false);
                    })
                }}
            />
            <SelectInputModal
                isOpen={isAddCommunityModalOpen}
                onRequestClose={closeAddCommunityModal}
                title={t('SMC_COMMUNITY_ADD_COMM')}
                label={t('SMC_COMMUNITY_SELECT')}
                options={
                    <>
                    <SMCOption value=""></SMCOption>
                    { communitySelectList.map(comm => (
                        <SMCOption value={comm} key={comm}>{comm}</SMCOption>
                    )) }
                    </>
                }
                defaultSelected=""
                onSuccess={communityToAdd => {
                    if (!communityToAdd) {
                        alert(t('SMC_COMMUNITY_EMPTY'));
                    } else if (members && members.find(member => member.name === communityToAdd && member.memberType === 'COMMUNITY')) {
                        alert(t('SMC_COMMUNITY_ALREADY_MEMBER', [communityToAdd, community]))
                    } else {
                        closeAddCommunityModal();
                        setIsPageLoading(true);
                        Api.addCommunityToCommunityMembers(community, communityToAdd)
                        .then(resp => {
                            loadMembers();
                        })
                        .catch(e => {
                            const errorMessage = getErrorMessage(e);
                            enqueueErrorSnackbar(errorMessage);
                        })
                        .finally(() => {
                            setIsPageLoading(false);
                        })
                    }
                }}
            />
        </div>
    );
}

export default React.memo(CommunityMembers);